import { Injectable, signal } from '@angular/core';
import { FuenteDeNombre } from '../../shared/utils/nombre-visible';

/** Nombre que la persona se pone en Ajustes → Perfil. */
export interface NombreLocal {
  firstName: string;
  lastName: string;
}

/** Misma clave que ya usaba el borrador del perfil: los nombres guardados antes se conservan. */
const PREFIJO = 'synapseguard_profile_draft_';

/**
 * Nombre editado en Perfil. Vive solo en la app (localStorage de este navegador):
 * NO se escribe en Auth0. El perfil y la barra lateral lo leen de aquí, así que al
 * guardar cambian los dos sin recargar.
 */
@Injectable({ providedIn: 'root' })
export class NombreLocalService {
  /** Sube con cada guardado para que quien lo lea en un effect/computed se refresque. */
  readonly version = signal(0);

  leer(user: { sub?: string | null; email?: string | null } | null | undefined): NombreLocal | null {
    try {
      const raw = localStorage.getItem(clave(user));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<NombreLocal>;
      if (typeof parsed.firstName !== 'string' || !parsed.firstName.trim()) return null;
      return {
        firstName: parsed.firstName.trim(),
        lastName: typeof parsed.lastName === 'string' ? parsed.lastName.trim() : '',
      };
    } catch {
      return null;
    }
  }

  guardar(user: { sub?: string | null; email?: string | null } | null | undefined, nombre: NombreLocal): void {
    try {
      localStorage.setItem(clave(user), JSON.stringify({
        firstName: nombre.firstName.trim(),
        lastName: nombre.lastName.trim(),
        email: typeof user?.email === 'string' ? user.email : '',
      }));
    } catch {
      // Sin almacenamiento (modo privado): el nombre dura hasta recargar.
    }
    this.version.update(v => v + 1);
  }

  /** El usuario de Auth0 con el nombre local encima, listo para `nombreVisible`. */
  aplicar<T extends FuenteDeNombre & { sub?: string | null }>(user: T): T {
    const local = this.leer(user);
    if (!local) return user;
    return { ...user, name: [local.firstName, local.lastName].filter(Boolean).join(' ') };
  }
}

function clave(user: { sub?: string | null; email?: string | null } | null | undefined): string {
  const id = typeof user?.sub === 'string' ? user.sub : (typeof user?.email === 'string' ? user.email : 'current');
  return `${PREFIJO}${id.trim().toLowerCase()}`;
}
